import { NgZone } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { MapboxLayersService } from '../../mapbox-layers.service';
import { MapboxClusterService } from '../../mapbox-cluster.service';
import {
  IEntityLayerManager,
  LayerVisibilityConfig,
  UpdateDataResult,
} from '../interfaces/entity-layer-manager.interface';

/**
 * Clase base abstracta que implementa comportamiento común
 * para todos los EntityLayerManagers
 *
 * Proporciona:
 * - Gestión de sources (normal y clustered)
 * - Gestión de visibilidad
 * - Conversión de entidades a GeoJSON
 * - Limpieza de recursos
 *
 * Las clases hijas solo necesitan:
 * - Definir propiedades (entityType, supportsClustering, etc.)
 * - Implementar createLayers() con la configuración específica
 * - Implementar entityToFeature() para convertir entidad a GeoJSON
 * - Implementar getIconExpression() para iconos específicos
 *
 * @template T Tipo de la entidad que maneja este manager
 */
export abstract class BaseEntityLayerManager<
  T = any,
> implements IEntityLayerManager<T> {
  // ========== PROPIEDADES ABSTRACTAS (deben ser definidas por clases hijas) ==========
  abstract readonly entityType: string;
  abstract readonly supportsClustering: boolean;
  abstract readonly clusterColor?: string;

  // ========== PROPIEDADES PROTEGIDAS ==========
  protected map: mapboxgl.Map | null = null;
  protected layersService: MapboxLayersService | null = null;
  protected clusterService: MapboxClusterService | null = null;
  protected popupCallback:
    | ((datos: any, tipo: string, coordinate?: [number, number]) => void)
    | null = null;
  protected ngZone: NgZone | null = null;

  protected isInitialized = false;
  protected isVisible = true;
  protected clusteringEnabled = false;

  // OPTIMIZACIÓN: Map para almacenar entidades sin serializar
  // Evita JSON.stringify/parse en cada updateData() y click
  protected entityMap = new Map<string, T>();

  // Filtro actual aplicado a los layers (null = sin filtro)
  protected currentFilter: mapboxgl.Expression | null = null;

  // OPTIMIZACIÓN: Debounce para updateData()
  // Útil cuando se reciben updates frecuentes (polling, websockets)
  private updateDebounceTimer?: ReturnType<typeof setTimeout>;
  private pendingEntities?: T[];

  // Control de click handlers: false = no registrar listeners propios (usar global del registry)
  protected registerOwnClickHandlers = false;

  // IDs de sources y layers (se construyen automáticamente)
  protected get sourceId(): string {
    return `${this.entityType}-source`;
  }

  protected get clusteredSourceId(): string {
    return `${this.entityType}-clustered-source`;
  }

  protected layerIds: string[] = [];

  // ========== CICLO DE VIDA ==========

  /**
   * Inicializa el manager
   * TEMPLATE METHOD: Llama a createSources() y createLayers() que pueden ser override
   */
  initialize(
    map: mapboxgl.Map,
    layersService: MapboxLayersService,
    clusterService: MapboxClusterService,
    popupCallback?: (
      datos: any,
      tipo: string,
      coordinate?: [number, number],
    ) => void,
    ngZone?: NgZone,
  ): void {
    if (this.isInitialized) {
      console.warn(`⚠️ ${this.entityType} manager ya está inicializado`);
      return;
    }

    this.map = map;
    this.layersService = layersService;
    this.clusterService = clusterService;
    this.popupCallback = popupCallback || null;
    this.ngZone = ngZone || null;

    // Template method: crear sources y layers
    // NOTA: Los iconos deben estar cargados ANTES de llamar a initialize()
    this.createSources();
    this.createLayers();

    // Configurar visibilidad inicial de clustering
    if (this.supportsClustering) {
      this.updateClusteringVisibility();
    }

    // Configurar event handlers (cursor, clicks)
    this.setupEventHandlers();

    this.isInitialized = true;
  }

  /**
   * Limpia todos los recursos
   */
  cleanup(): void {
    if (!this.map) return;

    // Remover layers
    this.layerIds.forEach((layerId) => {
      if (this.map!.getLayer(layerId)) {
        this.map!.removeLayer(layerId);
      }
    });

    // Remover sources
    const sourcesToRemove = [this.sourceId];
    if (this.supportsClustering) {
      sourcesToRemove.push(this.clusteredSourceId);
    }

    sourcesToRemove.forEach((sourceId) => {
      if (this.map!.getSource(sourceId)) {
        this.map!.removeSource(sourceId);
      }
    });

    // Limpiar referencias
    this.map = null;
    this.layersService = null;
    this.clusterService = null;
    this.isInitialized = false;
    this.layerIds = [];

    // IMPORTANTE: Resetear estados a valores por defecto
    this.isVisible = true;
    this.clusteringEnabled = false;
    this.currentFilter = null;

    // Limpiar debounce timer si existe
    if (this.updateDebounceTimer) {
      clearTimeout(this.updateDebounceTimer);
      this.updateDebounceTimer = undefined;
    }
    this.pendingEntities = undefined;

    // Limpiar entity map
    this.entityMap.clear();
  }

  /**
   * Desconecta listeners de nivel-mapa y markers DOM SIN remover sources/layers.
   *
   * Se llama en el cambio de estilo (registry.resetAll): setStyle() ya borró
   * sources/layers, pero los listeners (render/zoomend) y los markers DOM viven en
   * la instancia Map (que sobrevive al setStyle) y hay que removerlos a mano para
   * que no se acumulen ni queden huérfanos. Base: no-op. Override en subclases que
   * registren listeners propios (ej. luminarias con render/zoomend/donut).
   */
  detachListeners(): void {}

  /**
   * Obtiene el ID de una entidad
   * Asume que todas las entidades tienen _id
   */
  protected getEntityId(entity: any): string {
    return entity._id || entity.id || '';
  }

  /**
   * Almacena una entidad en el mapa interno
   * OPTIMIZACIÓN: Evita serializar con JSON.stringify
   */
  protected storeEntity(entity: T): void {
    const id = this.getEntityId(entity);
    if (id) {
      this.entityMap.set(id, entity);
    }
  }

  /**
   * Obtiene una entidad del mapa interno por ID
   * OPTIMIZACIÓN: Evita deserializar con JSON.parse
   */
  protected getEntity(id: string): T | undefined {
    return this.entityMap.get(id);
  }

  // ========== EVENT HANDLERS ==========

  /**
   * Configura event handlers para cursor
   * Automáticamente añade cursor pointer al pasar sobre entidades
   *
   * OPTIMIZACIÓN: Todos los event listeners se ejecutan FUERA de Angular Zone
   * para evitar change detection en cada evento de mouse
   */
  protected setupEventHandlers(): void {
    if (!this.map) return;

    // IMPORTANTE: Ejecutar todos los event listeners fuera de Angular Zone
    // Esto evita que cada evento de mouse dispare change detection
    const runOutside = this.ngZone
      ? (fn: () => void) => this.ngZone!.runOutsideAngular(fn)
      : (fn: () => void) => fn();

    runOutside(() => {
      // Registrar event handlers para todos los layers
      this.layerIds.forEach((layerId) => {
        if (!this.map!.getLayer(layerId)) return;

        // Solo agregar eventos a layers que NO sean de cluster (los clusters tienen sus propios handlers)
        if (
          layerId.includes('-cluster-layer') ||
          layerId.includes('-cluster-count-layer')
        ) {
          return;
        }

        // IMPORTANTE: No registrar mouseenter/mouseleave aquí
        // El EntityManagerRegistry los maneja globalmente para evitar duplicación

        // Click en entidades - solo registrar si registerOwnClickHandlers = true
        // (Por defecto el EntityManagerRegistry maneja clicks globalmente)
        if (!this.registerOwnClickHandlers) {
          return; // Salir sin registrar click handler propio
        }

        // Click en entidades - usar callback de popup
        this.map!.on('click', layerId, (e) => {
          if (!e.features || e.features.length === 0) return;

          // IMPORTANTE: Detener propagación del evento para evitar que se disparen
          // múltiples popups cuando hay markers superpuestos
          e.preventDefault();

          // OPTIMIZACIÓN: Consultar TODAS las features en el punto del click
          // para manejar correctamente markers superpuestos
          const allFeatures = this.map!.queryRenderedFeatures(e.point, {
            // CRÍTICO: filtrar a layers existentes. En Mapbox GL v3 un solo id
            // inexistente en `layers` hace que la query devuelva [] (anula el click).
            layers: this.layerIds.filter(
              (id) =>
                this.map!.getLayer(id) &&
                !id.includes('-cluster-layer') &&
                !id.includes('-cluster-count-layer'),
            ),
          });

          if (allFeatures.length === 0) return;

          // Si hay múltiples features superpuestas, tomar solo la primera visible
          // (Mapbox devuelve features ordenadas de arriba hacia abajo en el stack visual)
          const feature = allFeatures[0];
          const properties = feature.properties || {};

          try {
            // OPTIMIZACIÓN: Obtener entidad del Map en vez de deserializar JSON
            const entityId = properties['id'];
            const entity = this.getEntity(entityId);

            if (!entity) {
              return;
            }

            // Llamar al callback del popup si existe
            if (this.popupCallback) {
              // IMPORTANTE: Usar coordenadas del feature (centro del marker), NO del click
              // Esto asegura que la flecha del popup apunte al centro exacto del marker
              // y que el popup esté centrado horizontalmente sobre el marker
              const geometry = feature.geometry as GeoJSON.Point;
              const coordinate: [number, number] = geometry.coordinates as [
                number,
                number,
              ];

              // Permitir que subclases transformen el entity antes del popup
              const entityForPopup = this.getEntityForPopup(entity);

              // CRÍTICO: Volver a entrar en Angular Zone para actualizar UI del popup
              if (this.ngZone) {
                this.ngZone.run(() => {
                  this.popupCallback!(
                    entityForPopup,
                    this.entityType,
                    coordinate,
                  );
                });
              } else {
                this.popupCallback(entityForPopup, this.entityType, coordinate);
              }
            }
          } catch (err) {
            console.error(
              `❌ Error al obtener entidad de ${this.entityType}:`,
              err,
            );
            console.error('Properties recibidas:', properties);
          }
        });
      });
    });
  }

  /**
   * Hook para transformar la entidad antes de mostrarla en el popup
   * Las subclases pueden sobrescribir este método para agregar propiedades adicionales
   * Esta función puede ser llamada desde los entity-managers para agregar campos extra
   *
   * @param entity Entidad original
   * @returns Entidad transformada para el popup (por defecto, la misma entidad)
   */
  protected getEntityForPopup(entity: T): any {
    return entity;
  }

  /**
   * Obtiene una entidad por su ID y la transforma para el popup
   * PÚBLICO para uso del EntityManagerRegistry en el click handler global
   */
  public getEntityForPopupById(entityId: string): any | null {
    const entity = this.getEntity(entityId);
    if (!entity) {
      return null;
    }
    return this.getEntityForPopup(entity);
  }

  // ========== GESTIÓN DE DATOS ==========

  /**
   * Actualiza los datos en el mapa
   */
  updateData(entities: T[]): UpdateDataResult {
    if (!this.map || !this.isInitialized) {
      return {
        success: false,
        layersUpdated: [],
        sourcesUpdated: [],
        error: 'Manager no inicializado',
      };
    }

    try {
      // Convertir entidades a GeoJSON
      const features = entities.map((entity) => this.entityToFeature(entity));
      const geojson: GeoJSON.FeatureCollection<GeoJSON.Point> = {
        type: 'FeatureCollection',
        features,
      };

      const sourcesUpdated: string[] = [];

      // OPTIMIZACIÓN: Solo actualizar el source activo (no ambos)
      // Esto evita procesamiento duplicado cuando clustering está habilitado
      if (this.clusteringEnabled && this.supportsClustering) {
        // Clustering ON: actualizar solo source clustered
        const clusteredSource = this.map.getSource(
          this.clusteredSourceId,
        ) as mapboxgl.GeoJSONSource;
        if (clusteredSource) {
          clusteredSource.setData(geojson);
          sourcesUpdated.push(this.clusteredSourceId);
        } else {
          console.warn(
            `⚠️ Clustered source NO encontrado: ${this.clusteredSourceId}`,
          );
        }
      } else {
        // Clustering OFF: actualizar solo source normal
        const source = this.map.getSource(
          this.sourceId,
        ) as mapboxgl.GeoJSONSource;
        if (source) {
          source.setData(geojson);
          sourcesUpdated.push(this.sourceId);
        } else {
          console.warn(`⚠️ Source NO encontrado: ${this.sourceId}`);
        }
      }

      return {
        success: true,
        layersUpdated: this.layerIds,
        sourcesUpdated,
      };
    } catch (error) {
      console.error(
        `❌ Error actualizando datos de ${this.entityType}:`,
        error,
      );
      return {
        success: false,
        layersUpdated: [],
        sourcesUpdated: [],
        error: error instanceof Error ? error.message : 'Error desconocido',
      };
    }
  }

  /**
   * Actualiza los datos con debounce (agrupa múltiples llamadas)
   *
   * IMPORTANTE: Útil cuando se reciben updates frecuentes (polling cada 1-5s, websockets).
   * Agrupa múltiples llamadas en una sola actualización para mejorar performance.
   *
   * @param entities Array de entidades a renderizar
   * @param debounceMs Milisegundos de espera antes de actualizar (default: 150ms)
   *
   * @example
   * // Polling cada 2 segundos - con debounce agrupa updates
   * setInterval(() => {
   *   const vehiculos = await fetchVehiculos();
   *   vehiculosManager.updateDataDebounced(vehiculos, 150);
   * }, 2000);
   *
   * // WebSocket - agrupa updates rápidos
   * socket.on('vehiculo-update', (vehiculo) => {
   *   vehiculos[vehiculo.id] = vehiculo;
   *   vehiculosManager.updateDataDebounced(Object.values(vehiculos), 100);
   * });
   */
  updateDataDebounced(entities: T[], debounceMs: number = 150): void {
    // Guardar entidades pendientes
    this.pendingEntities = entities;

    // Limpiar timer anterior si existe
    if (this.updateDebounceTimer) {
      clearTimeout(this.updateDebounceTimer);
    }

    // Crear nuevo timer
    this.updateDebounceTimer = setTimeout(() => {
      if (this.pendingEntities) {
        this.updateData(this.pendingEntities);
        this.pendingEntities = undefined;
      }
      this.updateDebounceTimer = undefined;
    }, debounceMs);
  }

  /**
   * Cancela cualquier update pendiente del debounce
   */
  cancelPendingUpdate(): void {
    if (this.updateDebounceTimer) {
      clearTimeout(this.updateDebounceTimer);
      this.updateDebounceTimer = undefined;
    }
    this.pendingEntities = undefined;
  }

  /**
   * Limpia los datos sin destruir layers/sources
   */
  clearData(): void {
    if (!this.map || !this.isInitialized) return;

    const emptyGeoJSON: GeoJSON.FeatureCollection<GeoJSON.Point> = {
      type: 'FeatureCollection',
      features: [],
    };

    const source = this.map.getSource(this.sourceId) as mapboxgl.GeoJSONSource;
    if (source) {
      source.setData(emptyGeoJSON);
    }

    if (this.supportsClustering) {
      const clusteredSource = this.map.getSource(
        this.clusteredSourceId,
      ) as mapboxgl.GeoJSONSource;
      if (clusteredSource) {
        clusteredSource.setData(emptyGeoJSON);
      }
    }
  }

  // ========== VISIBILIDAD ==========

  setVisibility(visible: boolean): void {
    if (!this.map) return;

    this.isVisible = visible;
    const visibility = visible ? 'visible' : 'none';

    this.layerIds.forEach((layerId) => {
      if (this.map!.getLayer(layerId)) {
        this.map!.setLayoutProperty(layerId, 'visibility', visibility);
      }
    });
  }

  getVisibility(): boolean {
    return this.isVisible;
  }

  // ========== CLUSTERING ==========

  setClusteringEnabled(enabled: boolean): void {
    if (!this.supportsClustering) {
      console.warn(`⚠️ ${this.entityType} no soporta clustering`);
      return;
    }

    if (!this.map) return;

    const wasClusteringEnabled = this.clusteringEnabled;
    this.clusteringEnabled = enabled;

    // CRÍTICO: Copiar datos del source anterior al nuevo source
    if (wasClusteringEnabled !== enabled) {
      this.transferDataBetweenSources(wasClusteringEnabled, enabled);
    }

    // Mostrar/ocultar layers según estado de clustering
    // Esta lógica puede ser override por clases hijas si necesitan comportamiento custom
    this.updateClusteringVisibility();

    // Re-aplicar filtros a los layers que ahora están visibles
    if (this.currentFilter) {
      this.applyFiltersToLayers();
    }
  }

  getClusteringEnabled(): boolean {
    return this.clusteringEnabled;
  }

  /**
   * Transfiere datos entre el source normal y el source clusterizado
   * Esto es necesario cuando se cambia el estado de clustering
   */
  protected transferDataBetweenSources(
    wasClusteringEnabled: boolean,
    isClusteringEnabled: boolean,
  ): void {
    if (!this.map) return;

    const sourceFrom = wasClusteringEnabled
      ? this.clusteredSourceId
      : this.sourceId;
    const sourceTo = isClusteringEnabled
      ? this.clusteredSourceId
      : this.sourceId;

    const sourceFromInstance = this.map.getSource(
      sourceFrom,
    ) as mapboxgl.GeoJSONSource;
    const sourceToInstance = this.map.getSource(
      sourceTo,
    ) as mapboxgl.GeoJSONSource;

    if (!sourceFromInstance || !sourceToInstance) {
      console.warn(
        `⚠️ No se pudo transferir datos: source ${sourceFrom} o ${sourceTo} no encontrado`,
      );
      return;
    }

    // Obtener los datos del source origen
    // @ts-ignore - _data es una propiedad interna de GeoJSONSource
    const data: GeoJSON.FeatureCollection | null = sourceFromInstance._data;

    if (data && typeof data === 'object' && 'features' in data) {
      sourceToInstance.setData(data);
    } else {
      console.warn(`⚠️ No hay datos en ${sourceFrom} para transferir`);
    }
  }

  /**
   * Actualiza la visibilidad de layers según clustering
   * Puede ser override por clases hijas
   */
  protected updateClusteringVisibility(): void {
    if (!this.map) return;

    // Lógica por defecto: si clustering está habilitado, mostrar layers clustered
    // Si no, mostrar layers unclustered
    // Las clases hijas pueden override esto para lógica más compleja
  }

  // ========== FILTRADO ==========

  /**
   * Aplica un filtro a los layers de entidades
   *
   * IMPORTANTE: El filtro se aplica directamente en Mapbox sin regenerar GeoJSON.
   * Esto es 70-90% más rápido que filtrar el array y llamar updateData().
   *
   * @param filterExpression Expresión de filtro de Mapbox (ej: ['==', ['get', 'estado'], 'Activa'])
   *
   * @example
   * // Filtrar alarmas activas
   * alarmasManager.setFilter(['==', ['get', 'estado'], 'Activa']);
   *
   * // Filtrar luminarias OK o Encendidas
   * luminariasManager.setFilter(['in', ['get', 'estado'], ['literal', ['Ok', 'Encendida']]]);
   *
   * // Filtrar por múltiples condiciones
   * manager.setFilter(['all',
   *   ['==', ['get', 'tipo'], 'Tracker 4G'],
   *   ['>', ['get', 'velocidad'], 50]
   * ]);
   */
  setFilter(filterExpression: mapboxgl.Expression): void {
    if (!this.map || !this.isInitialized) {
      console.warn(
        `⚠️ ${this.entityType}: No se puede aplicar filtro, manager no inicializado`,
      );
      return;
    }

    this.currentFilter = filterExpression;
    this.applyFiltersToLayers();
  }

  /**
   * Elimina el filtro actual, mostrando todas las entidades
   */
  clearFilter(): void {
    if (!this.map || !this.isInitialized) return;

    this.currentFilter = null;
    this.applyFiltersToLayers();
  }

  /**
   * Obtiene el filtro actual (null si no hay filtro)
   */
  getCurrentFilter(): mapboxgl.Expression | null {
    return this.currentFilter;
  }

  /**
   * Aplica los filtros a todos los layers relevantes
   * Combina el filtro del usuario con los filtros de clustering
   */
  protected applyFiltersToLayers(): void {
    if (!this.map) return;

    // Aplicar filtro a cada layer registrado
    this.layerIds.forEach((layerId) => {
      const layer = this.map!.getLayer(layerId);
      if (!layer) return;

      // Determinar el filtro apropiado para este layer
      let finalFilter: mapboxgl.Expression | null = null;

      // CASO 1: Cluster layers - solo filtro de cluster (no aplicar filtro de usuario)
      if (
        layerId.includes('-cluster-layer') ||
        layerId.includes('-cluster-count-layer')
      ) {
        finalFilter = ['has', 'point_count'];
      }
      // CASO 2: Unclustered layers (cuando clustering está ON) - combinar filtros
      else if (layerId.includes('-unclustered-')) {
        if (this.currentFilter) {
          finalFilter = [
            'all',
            ['!', ['has', 'point_count']], // No es cluster
            this.currentFilter, // Filtro del usuario
          ];
        } else {
          finalFilter = ['!', ['has', 'point_count']];
        }
      }
      // CASO 3: Layers normales (sin clustering o clustering OFF) - solo filtro de usuario
      else {
        finalFilter = this.currentFilter;
      }

      // Aplicar el filtro al layer
      try {
        this.map!.setFilter(layerId, finalFilter as any);
      } catch (error) {
        console.error(`❌ Error aplicando filtro a layer ${layerId}:`, error);
      }
    });
  }

  // ========== CONFIGURACIÓN ==========

  getSourceId(): string {
    return this.sourceId;
  }

  getClusteredSourceId(): string | null {
    return this.supportsClustering ? this.clusteredSourceId : null;
  }

  getLayerIds(): string[] {
    return [...this.layerIds];
  }

  getSourceIds(): string[] {
    const ids = [this.sourceId];
    if (this.supportsClustering) {
      ids.push(this.clusteredSourceId);
    }
    return ids;
  }

  // ========== MÉTODOS ABSTRACTOS (deben ser implementados por clases hijas) ==========

  /**
   * Convierte una entidad a un Feature de GeoJSON
   * DEBE ser implementado por clases hijas
   */
  abstract entityToFeature(entity: T): GeoJSON.Feature<GeoJSON.Point>;

  /**
   * Obtiene la expresión para el icono
   * DEBE ser implementado por clases hijas
   */
  abstract getIconExpression(): mapboxgl.Expression | string;

  // ========== MÉTODOS PROTEGIDOS (template methods) ==========

  /**
   * Crea los sources de Mapbox
   * Puede ser override por clases hijas si necesitan configuración especial
   */
  protected createSources(): void {
    if (!this.map) return;

    // Source normal (sin clustering)
    if (!this.map.getSource(this.sourceId)) {
      this.map.addSource(this.sourceId, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
        buffer: 0, // Para point-only sources, mejora performance
      });
    }

    // Source clustered (solo si soporta clustering)
    if (
      this.supportsClustering &&
      !this.map.getSource(this.clusteredSourceId)
    ) {
      this.map.addSource(this.clusteredSourceId, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
        buffer: 0, // Para point-only sources, mejora performance
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 40,
      });
    }
  }

  /**
   * Crea los layers de Mapbox
   * DEBE ser implementado por clases hijas
   */
  protected abstract createLayers(): void;

  /**
   * Helper: Registra un layer ID para tracking
   */
  protected registerLayer(layerId: string): void {
    if (!this.layerIds.includes(layerId)) {
      this.layerIds.push(layerId);
    }
  }

  /**
   * Helper: Agrega un layer al mapa y lo registra
   */
  protected addLayer(layer: mapboxgl.AnyLayer): void {
    if (!this.map) return;

    // Agregar el layer solo si no existe; en ambos casos registrar el ID.
    // Caso restore(): el mapa principal ya tiene layers, pero layerIds fue reseteado.
    if (!this.map.getLayer(layer.id)) {
      this.map.addLayer(layer);
    }
    this.registerLayer(layer.id);
  }

  /**
   * Helper: Crea layers de clustering estándar
   * Puede ser usado por clases hijas que soporten clustering
   */
  protected createStandardClusterLayers(): void {
    if (!this.supportsClustering || !this.map || !this.clusterColor) return;

    const clusterLayerId = `${this.entityType}-cluster-layer`;
    const clusterCountLayerId = `${this.entityType}-cluster-count-layer`;

    // Layer de círculos del cluster
    this.addLayer({
      id: clusterLayerId,
      type: 'circle',
      source: this.clusteredSourceId,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': this.clusterColor,
        'circle-radius': ['step', ['get', 'point_count'], 20, 10, 30, 50, 40],
        'circle-opacity': 0.7,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    });

    // Layer de texto del cluster
    this.addLayer({
      id: clusterCountLayerId,
      type: 'symbol',
      source: this.clusteredSourceId,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12,
      },
      paint: {
        'text-color': '#ffffff',
      },
    });
  }
}
